import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { debounce } from 'es-toolkit';
import {
  Button,
  Dialog,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { MoreHoriz, Search } from '@mui/icons-material';

import { deleteMemberAtom, siteMembersAtom } from '@/interfaces/member';
import { SettingLayout } from '@/components/setting/SettingLayout';

import type { ChangeEvent, MouseEvent } from 'react';
import type { SiteMemberInfo } from '@/@types/user';
import InviteDialog from '@/components/setting/members/InviteDialog';

export default function Members() {
  const { data: members, isPending, refetch } = useAtomValue(siteMembersAtom);
  const { mutate: deleteMember, data: deleteSuccess } = useAtomValue(deleteMemberAtom);

  const [selectedMember, setSelectedMember] = useState<SiteMemberInfo>();
  const [query, setQuery] = useState('');
  const [tableData, setTableData] = useState<SiteMemberInfo[]>([]);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>();
  const [dialog, setDialog] = useState('');
  const { t } = useTranslation();

  useEffect(() => {
    if (isPending) return;
    setTableData(members);
  }, [isPending, members]);
  useEffect(() => {
    if (deleteSuccess === undefined) return;
    if (deleteSuccess) {
      window.alert(t('Settings.message.deleteMemberSuccess'));
      refetch();
    } else {
      window.alert(t('Settings.message.deleteMemberFailed'));
    }
  }, [deleteSuccess, refetch, t]);

  const filterMembers = useMemo(
    () =>
      debounce((query: string) => {
        if (query === '') {
          setTableData(members);
        } else {
          setTableData(members.filter((member) => member.info?.name.includes(query) || member.email.includes(query)));
        }
      }, 1000),
    [members],
  );
  function onQueryChange(e: ChangeEvent<HTMLInputElement>) {
    const { value } = e.target;
    filterMembers(value);
    setQuery(value);
  }
  function onInviteClick() {
    setDialog('INVITE');
  }
  function onActionClick(e: MouseEvent<HTMLButtonElement>, member: SiteMemberInfo) {
    setAnchorEl(e.currentTarget);
    setSelectedMember(member);
  }
  function onDeleteMemberClick() {
    const ok = window.confirm(
      `${t('Common.message.confirmDelete')}\n` +
        `${t('Common.label.name')}: ${selectedMember.info?.name}\n` +
        `${t('Settings.label.email')}: ${selectedMember.email}`,
    );
    if (ok) {
      setAnchorEl(null);
      deleteMember(selectedMember.id);
    }
  }
  const onDialogClose = useCallback(() => {
    setDialog('');
    refetch();
  }, [refetch]);

  return (
    <SettingLayout>
      <Stack gap={3}>
        <Stack direction="row" alignItems="center" sx={{ px: 2, py: 1, bgcolor: 'white' }}>
          <TextField
            value={query}
            onChange={onQueryChange}
            placeholder={t('Settings.label.searchMember')}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ width: '300px', mr: 'auto' }}
          />
          <Button variant="contained" onClick={onInviteClick}>
            {t('Settings.label.invite')}
          </Button>
        </Stack>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('Common.label.name')}</TableCell>
                <TableCell>{t('Settings.label.email')}</TableCell>
                <TableCell>{t('Settings.label.role')}</TableCell>
                <TableCell>{t('Common.label.groups')}</TableCell>
                <TableCell>{t('Common.label.action')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!isPending &&
                tableData.map((member) => {
                  const groupCount = Object.keys(member.permissions.groups).length;
                  return (
                    <TableRow key={member.id}>
                      <TableCell>{member.info?.name}</TableCell>
                      <TableCell>{member.email}</TableCell>
                      <TableCell>{t(`Common.label.${member.role.toLowerCase()}`)}</TableCell>
                      <TableCell>{groupCount}</TableCell>
                      <TableCell>
                        <IconButton sx={{ p: 1 }} onClick={(e) => onActionClick(e, member)}>
                          <MoreHoriz fontSize="medium" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              >
                <MenuItem onClick={onDeleteMemberClick}>{t('Settings.label.deleteMember')}</MenuItem>
              </Menu>
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>

      <Dialog open={dialog === 'INVITE'} onClose={onDialogClose}>
        <InviteDialog onClose={onDialogClose} />
      </Dialog>
    </SettingLayout>
  );
}
